import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, orderBy, Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../config/firebase/firebaseConfig';
import { ProgramModel } from '../types/program';

const PROGRAMS_COLLECTION = 'programs';

export class ProgramsService {
  static async loadPrograms(): Promise<ProgramModel[]> {
    try {
      const programsQuery = query(
        collection(db, PROGRAMS_COLLECTION),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(programsQuery);
      const programs: ProgramModel[] = [];

      snapshot.forEach((doc) => {
        const data = doc.data();
        programs.push({
          id: doc.id,
          title: data.title,
          description: data.description,
          photoUrl: data.photoUrl,
          url: data.url,
          createdAt: data.createdAt?.toDate() || new Date(),
        });
      });

      return programs;
    } catch (error) {
      console.error('Error loading programs:', error);
      throw error;
    }
  }

  static async createProgram(program: Omit<ProgramModel, 'id'>): Promise<void> {
    try {
      const docRef = await addDoc(collection(db, PROGRAMS_COLLECTION), {
        ...program,
        createdAt: Timestamp.fromDate(program.createdAt),
      });
      console.log('Program created with ID:', docRef.id);
    } catch (error) {
      console.error('Error creating program:', error);
      throw error;
    }
  }

  static async updateProgram(program: ProgramModel): Promise<void> {
    try {
      const docRef = doc(db, PROGRAMS_COLLECTION, program.id);
      await updateDoc(docRef, {
        title: program.title,
        description: program.description,
        photoUrl: program.photoUrl,
        url: program.url,
      });
    } catch (error) {
      console.error('Error updating program:', error);
      throw error;
    }
  }

  static async deleteProgram(programId: string): Promise<void> {
    try {
      const docRef = doc(db, PROGRAMS_COLLECTION, programId);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error deleting program:', error);
      throw error;
    }
  }

  static async uploadProgramImage(file: File): Promise<string> {
    try {
      const fileName = `${Date.now()}_${file.name}`;
      const storageRef = ref(storage, `programs/${fileName}`);

      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);

      return downloadURL;
    } catch (error) {
      console.error('Error uploading program image:', error);
      throw error;
    }
  }
}